import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { meshExportTrackingAtom } from '@/atoms/meshExport-progress.atom';
import { execute } from '@/graphql/execute';
import { getProjectJobsQuery } from '@/queries/project.query';

/**
 * Mounts once per project shell (alongside `useClipboxExportProgress`,
 * `useGroundClassifyProgress`, etc.).
 *
 * Same shape as `useClipboxExportProgress`: success is detected by the
 * MESH job transitioning to `PROCESSED` (not by a new layer appearing —
 * mesh export produces a download-only byproduct, no viewable layer).
 *
 * On success: resolves the loading toast into a success toast directing
 * the user to check Byproducts for download.
 *
 * On failure: resolves the loading toast into an error toast showing the
 * first line of the job's `errorLogs`.
 *
 * Polling cadence: explicit `setInterval(..., 2000)` driven by
 * `hasTrackers`, calling `refetch()` directly.
 */
export function useMeshExportProgress(projectId: string) {
  const { t } = useTranslation('project');
  const [trackers, setTrackers] = useAtom(meshExportTrackingAtom);

  const projectTrackers = trackers.filter((tr) => tr.projectId === projectId);
  const hasTrackers = projectTrackers.length > 0;

  const { data: jobsData, refetch: refetchJobs } = useQuery({
    queryKey: ['projectJobs', projectId],
    queryFn: () => execute(getProjectJobsQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  useEffect(() => {
    if (!hasTrackers || !projectId) return;
    // `cancelRefetch: false`: `refetch()` cancels the request still in
    // flight by default, so a response slower than this 2 s interval is
    // never delivered and the toast spins for ever. A tick JOINS the poll
    // already running (see `useDeviationProgress` for the reported case).
    const tick = () => {
      void refetchJobs({ cancelRefetch: false });
    };
    tick();
    const id = window.setInterval(tick, 2000);
    return () => window.clearInterval(id);
  }, [hasTrackers, projectId, refetchJobs]);

  useEffect(() => {
    if (!hasTrackers) return;
    const jobs = jobsData?.jobs ?? [];

    setTrackers((prev) => {
      const next = [...prev];
      let changed = false;

      for (let i = next.length - 1; i >= 0; i--) {
        const tracker = next[i];
        if (tracker.projectId !== projectId) continue;

        // Match the MESH job by batchId and type.
        const job = jobs.find((j) => j.batchId === tracker.batchId && j.type === 'MESH');
        if (!job) continue;

        if (job.status === 'FAILED') {
          // Surface the first line of errorLogs when available so the
          // user sees the actionable detail (OOM, timeout, empty mesh, etc.).
          const firstLine =
            typeof job.errorLogs === 'string' && job.errorLogs.trim().length > 0
              ? job.errorLogs.trim().split('\n')[0]
              : null;
          toast.error(firstLine ?? t('meshExport.failedToast', { name: tracker.sourceLayerName }), {
            id: tracker.toastId,
            duration: 6000,
          });
          next.splice(i, 1);
          changed = true;
          continue;
        }

        if (job.status === 'PROCESSED') {
          toast.success(t('meshExport.completedToast', { name: tracker.sourceLayerName }), {
            id: tracker.toastId,
            duration: 8000,
          });
          next.splice(i, 1);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [jobsData, hasTrackers, projectId, setTrackers, t]);
}
